/**
 * The configurations list's search, filters, order and bulk actions (HU-37).
 *
 * Unlike the video gallery, this list arrives whole — every configuration in one answer — so
 * everything here runs on screen: narrowing, ordering, and deciding what a selection can do. The
 * rules that can be wrong without anybody noticing are written down here, where they are tested:
 * what «ejecutable» means, where a configuration with no version sorts, and how a bulk delete that
 * half-worked is reported.
 */
import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import type { AnalysisConfigDetail, AnalysisConfigSummary } from '../model.js';
import { type ConfigChange, diffConfigs } from './config-diff.js';
import { type DayRange, dayRangeToInstants, withinDayRange } from './list-dates.js';
import { matchesSearch } from './list-search.js';
import { type SortOption, sortOptionOf, sortRows } from './list-sort.js';

// ── Filters ──────────────────────────────────────────────────────────────────

/**
 * The status filter.
 *
 * `runnable` is upstream's own verdict (a stored version **and** its engines document), never
 * «has a version number»: a configuration with a number and no document fails at dispatch, and a
 * filter that called it «ejecutable» would send somebody to launch with it. `not_runnable` is the
 * complement, so the two options split the list and nothing falls between them — and it is named
 * for what it is, not «sin versión»: a configuration with a number and no document lands there
 * while its row still reads «v3», and an option called «sin versión» would contradict it.
 */
export type ConfigStatusFilter = 'any' | 'runnable' | 'not_runnable';

const STATUS_FILTERS: readonly ConfigStatusFilter[] = ['any', 'runnable', 'not_runnable'];

/** Whether a select's value is one of the statuses, rather than trusting whatever came back. */
export function isConfigStatusFilter(value: unknown): value is ConfigStatusFilter {
  return STATUS_FILTERS.includes(value as ConfigStatusFilter);
}

/** What the funnel holds, as picked. */
export interface ConfigFilterState {
  status: ConfigStatusFilter;
  /** The date picker's value: ISO days, both inclusive; both `null` is no range. */
  created: DayRange;
}

export const EMPTY_CONFIG_FILTERS: Readonly<ConfigFilterState> = Object.freeze({
  status: 'any',
  created: Object.freeze({ from: null, to: null }),
});

function matchesStatus(config: AnalysisConfigSummary, status: ConfigStatusFilter): boolean {
  if (status === 'runnable') return config.runnable;
  if (status === 'not_runnable') return !config.runnable;

  return true;
}

/**
 * The configurations the search and the funnel leave, in the order they came.
 *
 * The search looks at the name and the description: those are the two things a person remembers a
 * configuration by, and the hash or the id are what the row shows, not what anybody types.
 */
export function filterConfigs(
  list: readonly AnalysisConfigSummary[],
  search: string,
  state: ConfigFilterState,
): AnalysisConfigSummary[] {
  return list.filter(
    (config) =>
      matchesSearch([config.name, config.description], search) &&
      matchesStatus(config, state.status) &&
      withinDayRange(config.createdAt, state.created),
  );
}

/**
 * How many kinds of filter are narrowing the list, for the funnel's badge.
 *
 * A range nobody could read filters nothing, so it does not count: a badge that claimed a filter
 * the list is not applying is the funnel lying about why rows are missing.
 */
export function activeConfigFilterCount(state: ConfigFilterState): number {
  const { from, to } = dayRangeToInstants(state.created);

  return [state.status !== 'any', Boolean(from ?? to)].filter(Boolean).length;
}

// ── Order ────────────────────────────────────────────────────────────────────

export type ConfigSortField = 'created_at' | 'name' | 'version';

/**
 * The orders the list offers. The first is the default: the newest first, which is how the list
 * has always opened. A configuration with no version sorts last in both version orders — turning
 * the order round should bring the other end of the numbers up, not a block of blanks.
 */
export const CONFIG_SORTS: readonly SortOption<ConfigSortField>[] = Object.freeze([
  { key: 'created_at:desc', label: 'Más recientes', orderBy: 'created_at', orderDir: 'desc' },
  { key: 'created_at:asc', label: 'Más antiguas', orderBy: 'created_at', orderDir: 'asc' },
  { key: 'name:asc', label: 'Nombre (A-Z)', orderBy: 'name', orderDir: 'asc' },
  { key: 'name:desc', label: 'Nombre (Z-A)', orderBy: 'name', orderDir: 'desc' },
  { key: 'version:desc', label: 'Versión más alta', orderBy: 'version', orderDir: 'desc' },
  { key: 'version:asc', label: 'Versión más baja', orderBy: 'version', orderDir: 'asc' },
] satisfies SortOption<ConfigSortField>[]);

export const DEFAULT_CONFIG_SORT = CONFIG_SORTS[0]!.key;

function sortValue(
  config: AnalysisConfigSummary,
  field: ConfigSortField,
): string | number | Date | null {
  if (field === 'name') return config.name;
  if (field === 'version') return config.version;

  return config.createdAt;
}

const BY_NAME = sortOptionOf(CONFIG_SORTS, 'name:asc');

/**
 * The configurations in an order, as a new array; an unknown key is the default order.
 *
 * Ties are broken by name first — many configurations share «v1» — so rows that tie read
 * alphabetically instead of in whatever order the API happened to answer, and do not swap places
 * between two visits.
 */
export function sortConfigs(
  list: readonly AnalysisConfigSummary[],
  key: string,
): AnalysisConfigSummary[] {
  const byName = sortRows(list, BY_NAME, sortValue);

  return sortRows(byName, sortOptionOf(CONFIG_SORTS, key), sortValue);
}

// ── The selection against the list ───────────────────────────────────────────

/**
 * The rows of the selected ids, in the order they were picked, or `null` if any is not in the list.
 *
 * Read from the whole list rather than from what is on screen: a configuration selected and then
 * filtered out is still selected, and its row here is the one the last refetch brought — so an
 * action checks today's «ejecutable», not the one it had when it was ticked.
 */
export function pickConfigs(
  ids: readonly number[],
  list: readonly AnalysisConfigSummary[],
): AnalysisConfigSummary[] | null {
  const byId = new Map(list.map((config) => [config.id, config]));
  const rows: AnalysisConfigSummary[] = [];

  for (const id of ids) {
    const row = byId.get(id);
    if (!row) return null;
    rows.push(row);
  }

  return rows;
}

/**
 * The selected ids the list no longer has — deleted from here, from another tab, by somebody else.
 *
 * A selection that kept them would count configurations that do not exist, and «Borrar 3» over two
 * rows is the one thing the count beside the box must never say.
 */
export function vanishedIds(
  ids: readonly number[],
  list: readonly AnalysisConfigSummary[],
): number[] {
  const present = new Set(list.map((config) => config.id));

  return ids.filter((id) => !present.has(id));
}

// ── Comparing two ────────────────────────────────────────────────────────────

/**
 * Why «Comparar» cannot be used on this selection, or `null` when it can.
 *
 * Exactly two, and both runnable: a configuration that never saved its engines has nothing to
 * compare, and a comparison of three is three comparisons nobody asked to read at once. `rows` is
 * `null` when the selection could not be matched to its rows — then nothing is compared rather than
 * something other than what the count says.
 */
export function compareRefusal(
  count: number,
  rows: readonly Pick<AnalysisConfigSummary, 'name' | 'runnable'>[] | null,
): string | null {
  if (count === 0) return 'Selecciona dos configuraciones para compararlas';
  if (count === 1) return 'Selecciona otra configuración para compararlas';
  if (count > 2) return `Se comparan de dos en dos: hay ${count} seleccionadas`;
  if (!rows || rows.length !== 2) return 'No se han podido reunir las dos configuraciones';

  const unversioned = rows.find((config) => !config.runnable);
  if (unversioned)
    return `«${unversioned.name}» no tiene motores guardados: no hay nada que comparar`;

  return null;
}

/**
 * The stored version to read a configuration's engines from, when its detail came without them.
 *
 * The detail is the first source — it is what the editor opens — but it can answer with no engines
 * (and no version) for a configuration the list calls «v10»: the list and the detail are two reads
 * upstream, and they do not always agree. The version the list names is immutable and has the
 * engines, so the comparison reads that rather than calling a configuration that runs every day
 * «sin motores». `null` while the detail is unread, when it has its engines, or when there is no
 * version to fall back on.
 */
export function storedVersionToRead(
  detail: Pick<AnalysisConfigDetail, 'engines'> | null | undefined,
  summary: Pick<AnalysisConfigSummary, 'version'>,
): number | null {
  if (!detail || detail.engines) return null;

  return summary.version;
}

/**
 * What differs in the engines of two configurations, or `null` if either never saved them.
 *
 * Wrapped in `{ engines }` because that is the shape `diffConfigs` keys its readable labels on —
 * passed bare, every row would read `gestures.targetFps`. The same diff the versions screen and
 * the groups screen use, so «qué cambia» has one answer wherever it is asked.
 */
export function engineChanges(
  left: Pick<AnalysisConfigDetail, 'engines'>,
  right: Pick<AnalysisConfigDetail, 'engines'>,
): ConfigChange[] | null {
  if (!left.engines || !right.engines) return null;

  return diffConfigs({ engines: left.engines }, { engines: right.engines });
}

// ── Deleting several ─────────────────────────────────────────────────────────

/** What happened to each configuration of a bulk delete, by id. */
export interface ConfigDeleteOutcome {
  deleted: number[];
  /** Refused because runs or groups hold it, or it is the active one: the rule doing its job. */
  inUse: number[];
  /** Failed for any other reason, or never tried because the reason would repeat. */
  failed: number[];
  /** The first failure, so the summary can say why rather than just how many. */
  firstFailure: unknown;
}

/**
 * Failures that say nothing about the configuration: the service is down, the connection is gone,
 * the session expired or the person lost the permission. The next call would get the same answer,
 * and there can be dozens of next calls, each one a usage count on the gateway. An expired session
 * is the costliest to go on with: every call would try the refresh again and fire the app's
 * «sesión caducada» toast and redirect to the login once more.
 */
const REPEATING_FAILURES: ReadonlySet<string> = new Set([
  ApiErrorCode.VP_UNAVAILABLE,
  ApiErrorCode.NETWORK_ERROR,
  ApiErrorCode.UNAUTHENTICATED,
  ApiErrorCode.PERMISSION_DENIED,
]);

function codeOf(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/**
 * Deletes configurations one after another, and classifies each answer.
 *
 * In turn, never in parallel: each delete makes the gateway count every run and group that holds
 * the configuration, and fifty of those at once is a burst nobody needs to save a few seconds.
 * «Ya no existe» counts as deleted — what was asked for is true, and the list refreshes either way.
 * A failure that would repeat stops the loop, and what was not tried counts as failed: it was not
 * deleted, and saying so is the honest part.
 */
export async function deleteConfigsInTurn(
  ids: readonly number[],
  remove: (id: number) => Promise<void>,
): Promise<ConfigDeleteOutcome> {
  const outcome: ConfigDeleteOutcome = { deleted: [], inUse: [], failed: [], firstFailure: null };

  for (const [index, id] of ids.entries()) {
    try {
      await remove(id);
      outcome.deleted.push(id);
    } catch (error) {
      const code = codeOf(error);

      if (code === ApiErrorCode.VP_CONFIG_NOT_FOUND) {
        outcome.deleted.push(id);
      } else if (code === ApiErrorCode.VP_CONFIG_IN_USE || code === ApiErrorCode.VP_CONFIG_ACTIVE) {
        // The active one is in use too — by Beon, for every interview with no configuration of its
        // own — and it is refused by the same rule, not by a failure.
        outcome.inUse.push(id);
      } else {
        outcome.failed.push(id);
        outcome.firstFailure ??= error;

        if (code !== null && REPEATING_FAILURES.has(code)) {
          outcome.failed.push(...ids.slice(index + 1));
          break;
        }
      }
    }
  }

  return outcome;
}

export type ConfigDeleteTone = 'success' | 'warning' | 'error';

/**
 * The one line a bulk delete ends with: «3 borradas · 2 en uso, no se pueden borrar · 1 falló».
 *
 * Every part that is not zero is said, because a toast reading «borradas» over a selection where
 * half stayed is how somebody concludes the rest went too. The tone follows what happened: all
 * gone is a success, some is a warning, none is an error.
 */
export function configDeleteSummary(
  outcome: Pick<ConfigDeleteOutcome, 'deleted' | 'inUse' | 'failed'>,
): {
  tone: ConfigDeleteTone;
  message: string;
} {
  const deleted = outcome.deleted.length;
  const inUse = outcome.inUse.length;
  const failed = outcome.failed.length;

  // «Ninguna», not «0 borradas»: a zero at the front of a toast reads as a typo.
  const parts = [
    deleted === 0 ? 'Ninguna borrada' : `${deleted} ${deleted === 1 ? 'borrada' : 'borradas'}`,
  ];
  if (inUse > 0) parts.push(`${inUse} en uso, no se ${inUse === 1 ? 'puede' : 'pueden'} borrar`);
  if (failed > 0) parts.push(`${failed} ${failed === 1 ? 'falló' : 'fallaron'}`);

  const tone: ConfigDeleteTone =
    inUse === 0 && failed === 0 ? 'success' : deleted > 0 ? 'warning' : 'error';

  return { tone, message: parts.join(' · ') };
}

/**
 * The count under the table: «12 configuraciones», or «3 de 12 configuraciones» when the search or
 * the funnel is narrowing — so a short list reads as filtered, not as configurations gone missing.
 */
export function configCountLine(shown: number, total: number): string {
  const noun = total === 1 ? 'configuración' : 'configuraciones';

  return shown === total ? `${total} ${noun}` : `${shown} de ${total} ${noun}`;
}
